<template>
  <b-card class="mb-2">
    <h5 class="mb-2">Atención médica</h5>

    <!-- Fila para especialidad general -->
    <b-row class="align-items-end" v-if="!isOdonto">
      <b-col md="5">
        <label>Especialidad</label>
        <v-select
          v-model="espSel"
          :options="especialidades"
          :reduce="o => o"
          label="text"
          placeholder="Seleccione especialidad"
        />
      </b-col>

      <b-col md="3">
        <label>Tipo de atención</label>
        <b-form-select v-model="tipoSel" :options="tipoOpts"/>
      </b-col>

      <b-col md="2">
        <label>Cantidad</label>
        <b-form-input type="number" min="1" step="1" v-model.number="cantidad"/>
      </b-col>

      <b-col md="2" class="d-flex align-items-end">
        <b-button
          variant="primary"
          class="mt-2 mt-md-0"
          :disabled="!canAdd"
          @click="agregar"
        >
          + Agregar
        </b-button>
      </b-col>
    </b-row>

    <!-- Fila especial para Odontología (CE) -->
    <b-row class="align-items-end" v-else>
      <b-col md="4">
        <label>Especialidad</label>
        <v-select
          v-model="espSel"
          :options="especialidades"
          :reduce="o => o"
          label="text"
          placeholder="Seleccione especialidad"
        />
      </b-col>
      <b-col md="4">
        <label>Procedimiento</label>
        <v-select
          v-model="procSel"
          :options="procOpciones"
          :reduce="o => o"
          label="label"
          placeholder="Buscar procedimiento…"
          @search="onSearchProc"
        />
      </b-col>
      <b-col md="2">
        <label>Cantidad</label>
        <b-form-input type="number" min="1" step="1" v-model.number="cantidad"/>
      </b-col>
      <b-col md="2" class="d-flex align-items-end">
        <b-button
          variant="primary"
          class="mt-2 mt-md-0"
          :disabled="!canAdd"
          @click="agregar"
        >
          + Agregar
        </b-button>
      </b-col>
    </b-row>

    <!-- LISTA -->
    <b-table
      v-if="itemsLocal.length"
      small bordered striped responsive
      :items="itemsLocal"
      :fields="fields"
      class="mt-2"
    >
      <template #cell(tipo)="ctx">
        {{ mostrarTipo(ctx.item.tipo) }}
      </template>
      <template #cell(especialidad_text)="ctx">
        <span>
          {{ ctx.item.especialidad_text }}
          <template v-if="ctx.item.proc_descripcion"> / {{ ctx.item.proc_descripcion }}</template>
        </span>
      </template>

      <template #cell(acciones)="ctx">
        <div class="text-right">
          <b-button size="sm" variant="danger" @click="eliminar(ctx.index)" title="Eliminar">
            <i class="fas fa-times"/>
          </b-button>
        </div>
      </template>
    </b-table>

    <b-alert v-else variant="danger" class="mt-2" show>
      <i class="fa-solid fa-triangle-exclamation mr-1"></i>
      No ha registrado ninguna atención
    </b-alert>
  </b-card>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import {
  BCard, BRow, BCol, BFormSelect, BButton, BTable, BAlert, BFormInput
} from 'bootstrap-vue'
import { especialidadService } from '@/shared/services'
import { procedimientosService } from '@/views/procesoclinico/services/procedimientosService'

export default {
  name: 'PaqAtencionMedica',
  components: { vSelect, BCard, BRow, BCol, BFormSelect, BButton, BTable, BAlert, BFormInput },
  props: {
    // Usa :items.sync desde el padre. Guardaremos objetos del tipo:
    // { tipo: 'VIRTUAL'|'PRESENCIAL', especialidad_id: string|number, especialidad_text: string }
    items: { type: Array, default: () => [] }
  },
  data () {
    return {
      especialidades: [],         // [{ value, text }]
      espSel: null,               // opción completa {value,text}
      tipoSel: null,              // 'VIRTUAL' | 'PRESENCIAL'
      itemsLocal: [...this.items],
      cantidad: 1,
      procSel: null,
      procOpciones: [],
      tipoOpts: [
        { value: 'VIRTUAL',    text: 'Virtual' },
        { value: 'PRESENCIAL', text: 'Presencial' },
      ],
      fields: [
        { key: 'tipo',              label: 'Tipo' },
        { key: 'especialidad_text', label: 'Especialidad' },
        { key: 'cantidad',          label: 'Cant.', tdClass: 'text-center', thClass: 'text-center' },
        { key: 'acciones',          label: 'Acciones', class: 'text-right' },
      ],
    }
  },
  computed: {
    canAdd () {
      if (!this.espSel || !this.cantidad || this.cantidad < 1) return false
      if (this.isOdonto) return !!this.procSel // requiere procedimiento
      return !!this.tipoSel
    },
    isOdonto () {
      const ODONTO_COD = '0000000134'
      const cod = this.espSel && (this.espSel.codigo || this.espSel.codEsp)
      return cod === ODONTO_COD
    }
  },
  watch: {
    itemsLocal: {
      handler (val) { this.$emit('update:items', val) },
      deep: true
    },
    // Si selecciona Odontología, forzamos tipo = PRESENCIAL
    espSel (v) {
      if (v && this.isOdonto) this.tipoSel = 'PRESENCIAL'
    }
  },
  async created () {
    try {
      // Debe devolver: [{ value, text }, ...]
      this.especialidades = await especialidadService.getOptionsSelect() || []
    } catch (e) {
      this.especialidades = []
    }
    // Asegura cantidad por defecto en ítems existentes
    this.itemsLocal = (this.items || []).map(it => ({ ...it, cantidad: it.cantidad ?? 1 }))
  },
  methods: {
    mostrarTipo (t) {
      return t === 'VIRTUAL' ? 'Virtual' : (t === 'PRESENCIAL' ? 'Presencial' : '-')
    },
    async onSearchProc (q) {
      if (!q || q.trim().length < 3) { this.procOpciones = []; return }
      const rs = await procedimientosService.searchSns(q.trim())
      this.procOpciones = (rs || []).map(p => ({
        id: p.value ?? p.id,
        codigo: p.codigo,
        descripcion: p.text ?? p.descripcion,
        label: `(${p.codigo}) ${p.text ?? p.descripcion}`
      }))
    },
    agregar () {
      if (!this.canAdd) return

      const nuevo = {
        tipo: this.isOdonto ? 'PRESENCIAL' : this.tipoSel,
        especialidad_id: this.espSel.value,
        especialidad_text: this.espSel.text,
        proc_id: this.isOdonto ? (this.procSel?.id ?? null) : null,
        proc_codigo: this.isOdonto ? (this.procSel?.codigo ?? null) : null,
        proc_descripcion: this.isOdonto ? (this.procSel?.descripcion ?? null) : null,
        cantidad: this.cantidad || 1,
      }

      // Si ya existe misma combinación, suma cantidad; si no, agrega
      const idx = this.itemsLocal.findIndex(x =>
        x.tipo === nuevo.tipo &&
        x.especialidad_id === nuevo.especialidad_id &&
        (this.isOdonto ? x.proc_id === nuevo.proc_id : true)
      )
      if (idx !== -1) {
        const copia = [...this.itemsLocal]
        const prev = copia[idx]
        copia[idx] = { ...prev, cantidad: Number(prev.cantidad || 1) + Number(nuevo.cantidad || 1) }
        this.itemsLocal = copia
      } else {
        this.itemsLocal.push(nuevo)
      }

      // reset mínimos
      this.tipoSel = null
      this.cantidad = 1
      this.procSel = null
      // mantengo la especialidad por conveniencia (si quieres resetearla: this.espSel = null)
    },
    eliminar (idx) {
      this.itemsLocal.splice(idx, 1)
    }
  }
}
</script>

<style scoped>
/* sin estilos especiales */
</style>
